import { useState } from 'react';

import { BannerPlacement } from '../../../../typings/enums';
import { window } from '../../../globals';
import BannerManager from '../../BannerManager';
import Button from '../../generic/Button';
import AuthCard from '../components/AuthCard';
import ForgotPasswordLink from '../components/ForgotPasswordLink';
import SignInSignUpToggle from '../components/SignInSignUpToggle';
import SocialLoginButtons from '../components/SocialLoginButtons';
import useAuthState from '../hooks/useAuthState';
import useResolveUser from '../hooks/useResolveUser';
import AuthLayout from '../layout/AuthLayout';
import EnterIdentifierStep from '../steps/EnterIdentifierStep';
import type { ResolveUserResponse } from '../../../../shared/types/routes/auth/v2';
import type { RouterProps } from '../../../types';

import styles from '../Login.module.less';

export type LoginPageProps = {
  location: RouterProps['location'];
};

/**
 * The login flow's state machine, held in a single `useState` (no Context, no reducer). Each phase renders one step
 * inside the shared auth chrome; the active step advances the phase. Currently the identifier-entry phase plus a
 * temporary `resolved` phase that makes the resolve-user round-trip observable.
 */
export type LoginPhase =
  | { kind: 'enter_identifier' }
  | { kind: 'resolved'; identifier: string; response: ResolveUserResponse };

const Login = ({ location }: LoginPageProps) => {
  const [identifier, setIdentifier] = useState(location.query.email ?? '');
  const [phase, setPhase] = useState<LoginPhase>({ kind: 'enter_identifier' });
  const { data: authState } = useAuthState();
  const resolveUser = useResolveUser();

  const oauthProviders = authState?.kind === 'idle' ? authState.oauthProviders : [];

  const handleSubmit = () => {
    if (phase.kind !== 'enter_identifier' || !identifier || resolveUser.isLoading) {
      return;
    }

    resolveUser.mutate(
      { identifier, context: 'login' },
      {
        onSuccess: response => {
          if (response) {
            setPhase({ kind: 'resolved', identifier, response });
          }
        },
      },
    );
  };

  return (
    <AuthLayout>
      <AuthCard onSubmit={handleSubmit}>
        <h1 className={styles.heading}>Sign In</h1>
        {phase.kind === 'enter_identifier' && (
          <div className={styles.fieldGroup}>
            <EnterIdentifierStep identifier={identifier} onIdentifierChange={setIdentifier} />
            <ForgotPasswordLink />
          </div>
        )}
        {phase.kind === 'resolved' && (
          <pre data-test-id="resolved-phase">{JSON.stringify(phase.response, null, 2)}</pre>
        )}
        <Button isFullWidth size="large" isDisabled={resolveUser.isLoading} onClick={handleSubmit}>
          Next
        </Button>
        {phase.kind === 'enter_identifier' && <SocialLoginButtons providers={oauthProviders} />}
      </AuthCard>
      <SignInSignUpToggle identifier={identifier} next={location.query.next} />
      {window.on_prem && <BannerManager placement={BannerPlacement.Login} />}
    </AuthLayout>
  );
};

export default Login;
